/**
 * Shared visual tokens for the two /submit paths.
 *
 * Extracted the moment the second path arrived rather than copied: the Sleeper
 * form and the results form sit behind a tab toggle, so a font size or input
 * height that drifts between them is visible in a single click.
 */

import { theme } from "../../data/theme.js";

export const C = theme.colors;

export const SZ = { micro: 12, small: 13, body: 15, sub: 17, hero: 28 };

export const card = {
  background: C.bg.surface,
  border: `1px solid ${C.border.default}`,
  borderRadius: theme.radius.xl,
  padding: 18,
};

// 16px font size is load-bearing, not a style choice: anything smaller makes
// iOS Safari zoom the viewport on focus, and the user lands mid-form at 1.5x.
export const input = {
  width: "100%",
  boxSizing: "border-box",
  padding: "12px 12px",
  fontSize: 16,
  borderRadius: theme.radius.lg,
  background: C.bg.deep,
  border: `1px solid ${C.border.default}`,
  color: C.text.primary,
};

export const label = { fontSize: SZ.small, color: C.text.secondary, display: "block", marginBottom: 6 };

/**
 * A league's own name, wherever /rate prints one.
 *
 * 🔴 ORANGE HERE IS IDENTITY, NOT RANKING (Ben, Sep 3 2026). The report heading
 * and the saved-league chips print a name inside a line of metadata — "Strong
 * Island Fantasy · 12 teams · $200 budget" — and the name was the same grey as
 * the team count beside it, so the one thing on the line that says WHOSE league
 * this is read as the least of the three facts. It takes the accent the page
 * titles are built from.
 *
 * It is deliberately a flat colour rather than the titles' gradient: a
 * background-clipped gradient on a short inline span inside a sentence clips to
 * the span's OWN box, so two names of different lengths would come out two
 * different colours.
 *
 * 🔴 AND IT IS brand.orange, NOT brand.accent, BECAUSE brand.accent IS THE B
 * GRADE. GRADE_COLOR maps A to green, B to brand.accent, C to warning and the
 * rest to danger, so a league name in the accent would be the page's own B
 * colour sitting a line above a column of letter grades. brand.orange is the
 * other stop of the very gradient the page titles are built from and it belongs
 * to no grade at all, which is exactly the property this needs: orange here
 * means "this is the object of the page", never "this is good".
 *
 * WHAT DOES NOT TAKE IT: the grades and the monochrome build chips, both
 * untouched.
 */
export const leagueNameText = { color: C.brand.orange, fontWeight: 800 };

/**
 * A section heading on /rate's landing.
 *
 * 🔴 ONE TOKEN, SO THREE SECTIONS CANNOT BE THREE SIZES (Ben, Sep 3 2026). The
 * landing's blocks were headed by three different things: a small-caps eyebrow
 * over the saved leagues, a 17px line over the paste box, and a form label over
 * the Sleeper input. A reader scanning for "where do I start" had no heading
 * level to scan. All three now take this, so promoting one cannot leave the
 * others behind.
 *
 * A step below the page title rather than level with it: the h1 is the gradient
 * hero and there is one of it, and a section that competed with it would flatten
 * the page into a list of equals.
 */
export const sectionTitle = {
  fontSize: 20,
  fontWeight: 800,
  lineHeight: 1.25,
  color: C.text.primary,
  marginBottom: 6,
};

/**
 * A neutral label pill: "Beta", and anything else that names rather than ranks.
 *
 * 🔴 MONOCHROME, ON BuildChip's RULE AND FOR ITS REASON. Colour ranks things.
 * This page spends its palette on grades - green A, orange B, yellow C, red D -
 * so a coloured tag beside a feature name would read as a verdict on the
 * feature. A tag is a label, not a score, and the grade colours stay out of it.
 */
export const tagPill = {
  display: "inline-block",
  flexShrink: 0,
  padding: "1px 7px",
  borderRadius: theme.radius.sm,
  border: `1px solid ${C.border.default}`,
  background: C.bg.deep,
  color: C.text.secondary,
  fontSize: SZ.micro,
  fontWeight: 700,
  letterSpacing: "0.04em",
  whiteSpace: "nowrap",
  lineHeight: 1.5,
};

export const primaryBtn = (enabled) => ({
  padding: "12px 20px",
  fontSize: SZ.body,
  fontWeight: 800,
  borderRadius: theme.radius.lg,
  border: "none",
  background: C.brand.gradientBtn,
  color: "#fff",
  cursor: enabled ? "pointer" : "default",
  opacity: enabled ? 1 : 0.6,
});

export const fineText = { marginTop: 14, fontSize: SZ.micro, color: C.text.faint, lineHeight: 1.5 };

export const successText = { fontSize: SZ.sub, fontWeight: 700, color: C.status.success, lineHeight: 1.4 };

export const errorText = { marginTop: 10, fontSize: SZ.small, color: C.status.danger };
